import React from 'react';
import { CHARACTER_IDS } from '../types/magical';
import { getCharacterColors } from '../types/characterColors';

const MagicalHistory = ({ toolData, undoLastLap, deleteLastLap, resetAll, updateLapExcludeFlag }) => {
  const getLapStats = (lap) => {
    const suikaToCzRate = lap.suikaCount > 0 ? 
      ((lap.czCount / lap.suikaCount) * 100).toFixed(1) : '0.0';
    
    const totalEyeCatch = CHARACTER_IDS.reduce((sum, charId) => 
      sum + lap.eyeCatches[charId].strong + lap.eyeCatches[charId].weak, 0);
    
    const totalStrong = CHARACTER_IDS.reduce((sum, charId) => 
      sum + lap.eyeCatches[charId].strong, 0);
    const totalWeak = CHARACTER_IDS.reduce((sum, charId) => 
      sum + lap.eyeCatches[charId].weak, 0);
    
    const strongRate = totalEyeCatch > 0 ? ((totalStrong / totalEyeCatch) * 100).toFixed(1) : '0.0';
    const weakRate = totalEyeCatch > 0 ? ((totalWeak / totalEyeCatch) * 100).toFixed(1) : '0.0';

    return { suikaToCzRate, totalEyeCatch, strongRate, weakRate };
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">ラップ履歴</h2>
        <button
          onClick={resetAll}
          className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded"
        >
          リセット
        </button>
      </div>
      
      {toolData.laps.length === 0 ? (
        <p className="text-gray-500 text-center py-8">まだラップがありません</p>
      ) : (
        <div className="space-y-4">
          {[...toolData.laps].reverse().map((lap, index) => {
            const { suikaToCzRate, totalEyeCatch, strongRate, weakRate } = getLapStats(lap);
            
            // スルー回数の計算
            const getThroughCount = () => {
              const currentLapIndex = toolData.laps.length - index - 1;
              
              // 現在のラップより前の履歴から最後のAT当選を探す
              let lastATIndex = -1;
              for (let i = currentLapIndex - 1; i >= 0; i--) {
                if (toolData.laps[i].bonusType === 'at') {
                  lastATIndex = i;
                  break;
                }
              }
              
              // 最後のAT当選の次のラップから現在のラップの直前までのボーナス当選回数をカウント
              // 現在のラップ自体は含めない
              let throughCount = 0;
              const startIndex = lastATIndex + 1;
              for (let i = startIndex; i < currentLapIndex; i++) {
                if (toolData.laps[i] && toolData.laps[i].bonusType === 'bb') {
                  throughCount++;
                }
              }
              
              return throughCount;
            };
            
            const throughCount = getThroughCount();
            
            const isATLap = lap.bonusType === 'at';
            const borderClass = isATLap ? 'border-red-500 border-2' : 'border';
            const bgClass = isATLap ? 'bg-red-50' : 'bg-gray-50';
            const titleText = isATLap ? 'AT当選' : 'ボーナス当選';
            const titleColor = isATLap ? 'text-red-700' : 'text-gray-900';
            
            return (
              <div key={lap.lapId} className={`${borderClass} rounded-lg p-4 ${bgClass}`}>
                <div className="mb-3">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className={`font-semibold text-lg ${titleColor}`}>{titleText}</h3>
                    {index === 0 && (
                      <div className="flex gap-2">
                        <button
                          onClick={undoLastLap}
                          className="px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded"
                        >
                          戻す
                        </button>
                        <button
                          onClick={deleteLastLap}
                          className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded"
                        >
                          削除
                        </button>
                      </div>
                    )}
                  </div>
                  <span className="text-sm text-gray-500">
                    {throughCount}スルー - {new Date(lap.createdAt).toLocaleString('ja-JP')}
                  </span>
                </div>
                
                <div className="grid grid-cols-3 md:grid-cols-7 gap-2 mb-3 text-xs">
                  <div className="text-center">
                    <div className="text-gray-600">スイカ</div>
                    <div className="font-semibold">{lap.suikaCount}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-600">CZ</div>
                    <div className="font-semibold">{lap.czCount}</div>
                  </div>
                  <div className="flex items-center justify-center">
                    <button
                      onClick={() => updateLapExcludeFlag(lap.lapId, !(lap.excludeFromStats || false))}
                      className={`px-2 py-1 text-xs font-semibold rounded ${
                        lap.excludeFromStats 
                          ? 'bg-gray-500 hover:bg-gray-600 text-white' 
                          : 'bg-orange-500 hover:bg-orange-600 text-white'
                      }`}
                    >
                      {lap.excludeFromStats ? 'スイカCZを統計に含む' : 'スイカCZを統計から除外'}
                    </button>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-600">CZ率</div>
                    <div className="font-semibold">{suikaToCzRate}%</div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-600">アイキャッチ</div>
                    <div className="font-semibold">{totalEyeCatch}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-600">強率</div>
                    <div className="font-semibold text-red-600">{strongRate}%</div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-600">弱率</div>
                    <div className="font-semibold text-blue-600">{weakRate}%</div>
                  </div>
                </div>

                {/* アイキャッチ詳細 */}
                <div className="border-t pt-3">
                  <h4 className="text-sm font-semibold mb-2">アイキャッチ詳細</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                    {CHARACTER_IDS.map(charId => {
                      const colors = getCharacterColors(charId);
                      const strong = lap.eyeCatches[charId].strong;
                      const weak = lap.eyeCatches[charId].weak;
                      const total = strong + weak;
                      
                      if (total === 0) return null;
                      
                      const charRate = totalEyeCatch > 0 ? ((total / totalEyeCatch) * 100).toFixed(1) : '0.0';
                      
                      return (
                        <div key={charId} className={`flex justify-between ${colors.background} ${colors.border} border px-2 py-1 rounded`}>
                          <span className={`${colors.text.replace('text-white', 'text-gray-800')} font-medium text-xs`}>{colors.name}:</span>
                          <span className="font-medium text-xs">
                            {strong > 0 && `強${strong}`}
                            {strong > 0 && weak > 0 && ' '}
                            {weak > 0 && `弱${weak}`}
                            <span className="text-gray-500 ml-1">({charRate}%)</span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MagicalHistory;